import { useCallback, useContext, useRef, useState } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { SettingsContext } from "../../../contexts/SettingsContext";
import { useInviteStatus } from "../invite/useInviteStatus";
import {
  emitTradeExecuted,
  TradeExecutedDetail,
} from "../../../utils/tradeEvents";
import { usePlacePerpOrder } from "./usePerpOrder.place";
import { useClosePerpPosition } from "./usePerpOrder.close";
import { useCancelPerpOrder, useCancelPerpOrdersBatch } from "./usePerpOrder.cancel";
import {
  useCancelConflictingCloseOrders,
  useCancelRestingCloseOrders,
} from "./usePerpOrder.cancelHelpers";

// Public API re-exports — importers resolve these from this path unchanged.
export type {
  IPerpOrderParams,
  IClosePerpPositionOptions,
} from "./usePerpOrder.types";

export const usePerpOrder = () => {
  const { account, masterAccount, apiKey, apiSecret } = useContext(AuthContext);
  // One standing preference, read here so every market order and market close
  // carries it — the trade form, the close modal and "close all" alike —
  // without each caller having to remember to pass it.
  const { slippageBps } = useContext(SettingsContext);
  const { data: inviteStatus } = useInviteStatus();
  const [isLoading, setIsLoading] = useState(false);
  const walletTransactionInFlightRef = useRef(false);

  const revalidatePerpDataSoon = useCallback((detail?: TradeExecutedDetail) => {
    // Announce the confirmed trade; every data owner converges itself.
    //
    // This replaced a broad `mutate(keyFilter)` sweep that fired a fixed ladder
    // across every trading endpoint on each trade. Two problems with it: it
    // could never reach `/positions` or `/open-orders` (SWR skips `$inf$` keys
    // in a key-filter mutate), and for the endpoints it did reach it refetched
    // unconditionally rather than stopping once the server had caught up.
    emitTradeExecuted(detail);
  }, []);


  const placePerpOrder = usePlacePerpOrder({
    account,
    masterAccount,
    inviteStatus,
    slippageBps,
    walletTransactionInFlightRef,
    setIsLoading,
    revalidatePerpDataSoon,
  });

  const cancelConflictingCloseOrders = useCancelConflictingCloseOrders({
    account,
    apiKey,
    apiSecret,
    revalidatePerpDataSoon,
  });

  const cancelRestingCloseOrders = useCancelRestingCloseOrders({
    account,
    apiKey,
    apiSecret,
    revalidatePerpDataSoon,
  });

  const closePerpPosition = useClosePerpPosition({
    account,
    masterAccount,
    inviteStatus,
    slippageBps,
    walletTransactionInFlightRef,
    setIsLoading,
    revalidatePerpDataSoon,
    cancelConflictingCloseOrders,
  });

  const cancelPerpOrder = useCancelPerpOrder({
    account,
    masterAccount,
    inviteStatus,
    walletTransactionInFlightRef,
    setIsLoading,
    revalidatePerpDataSoon,
  });

  const cancelPerpOrdersBatch = useCancelPerpOrdersBatch({
    account,
    masterAccount,
    inviteStatus,
    walletTransactionInFlightRef,
    setIsLoading,
    revalidatePerpDataSoon,
  });

  return {
    placePerpOrder,
    closePerpPosition,
    cancelRestingCloseOrders,
    cancelPerpOrder,
    cancelPerpOrdersBatch,
    isLoading,
  };
};
